import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { generateParticipationCertificate } from '../../utils/certificateGenerator';
import type { CertificateData, OverlayMode } from '../../utils/certificateGenerator';

interface GenerateCertificateProps {
  domain: string;
}

interface CertificateTemplate {
  id: string;
  name: string;
  description: string;
  templateUrl: string;
  templateBase64?: string; // Base64 encoded template for PDF generation
}

interface Student {
  id: string;
  enrollmentId: string;
  name: string;
  class: string;
  section: string;
  rollNumber: string;
  parentName?: string;
  dateOfBirth?: string;
  email?: string;
}

const GenerateCertificate: React.FC<GenerateCertificateProps> = ({ domain }) => {
  // State for templates
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CertificateTemplate | null>(null);

  // State for student selection mode
  const [selectionMode, setSelectionMode] = useState<'class' | 'enrollment'>('class');

  // State for class/section filter
  const [classes, setClasses] = useState<string[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');

  // State for enrollment ID search
  const [enrollmentSearch, setEnrollmentSearch] = useState<string>('');

  // State for students
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());

  // State for custom fields
  const [customFields, setCustomFields] = useState<Record<string, string>>({});

  // State for PDF generation fields
  const [pdfFields, setPdfFields] = useState({
    programName: 'Student Innovation and Leadership Development Program',
    eventDate: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
    directorName: '',
    directorTitle: 'Program Director',
    trainerName: '',
    trainerTitle: 'Workshop Trainer'
  });

  // State for overlay mode
  const [overlayMode, setOverlayMode] = useState<OverlayMode>('studentNameOnly');

  // State for UI
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewStudent, setPreviewStudent] = useState<Student | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
    loadAllStudents();
  }, [domain]);

  // Load unique classes from students
  useEffect(() => {
    const uniqueClasses = [...new Set(students.map(s => s.class))].sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    });
    setClasses(uniqueClasses);
  }, [students]);

  // Load sections when class changes
  useEffect(() => {
    if (selectedClass) {
      const classSections = [...new Set(
        students
          .filter(s => s.class === selectedClass)
          .map(s => s.section)
      )].sort();
      setSections(classSections);
      setSelectedSection('');
    } else {
      setSections([]);
      setSelectedSection('');
    }
  }, [selectedClass, students]);

  // Filter students based on selection mode
  useEffect(() => {
    if (selectionMode === 'class') {
      if (selectedClass && selectedSection) {
        const filtered = students.filter(
          s => s.class === selectedClass && s.section === selectedSection
        ).sort((a, b) => {
          const rollA = parseInt(a.rollNumber) || 0;
          const rollB = parseInt(b.rollNumber) || 0;
          return rollA - rollB;
        });
        setFilteredStudents(filtered);
      } else {
        setFilteredStudents([]);
      }
    } else {
      if (enrollmentSearch.trim()) {
        const searchLower = enrollmentSearch.toLowerCase();
        const filtered = students.filter(
          s => s.enrollmentId?.toLowerCase().includes(searchLower) ||
               s.name.toLowerCase().includes(searchLower)
        );
        setFilteredStudents(filtered);
      } else {
        setFilteredStudents([]);
      }
    }
    setSelectedStudents(new Set());
  }, [selectionMode, selectedClass, selectedSection, enrollmentSearch, students]);

  const loadTemplates = async () => {
    if (!domain) return;
    setLoading(true);
    try {
      const templatesRef = collection(db, `school-data/${domain}/certificate-templates`);
      const snapshot = await getDocs(templatesRef);
      const templateList: CertificateTemplate[] = [];
      snapshot.forEach((doc) => {
        templateList.push({ id: doc.id, ...doc.data() } as CertificateTemplate);
      });
      setTemplates(templateList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadAllStudents = async () => {
    if (!domain) return;
    setLoadingStudents(true);
    try {
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);
      const studentList: Student[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        studentList.push({
          id: doc.id,
          enrollmentId: data.enrollmentId || doc.id,
          name: data.name || '',
          class: data.class || '',
          section: data.section || '',
          rollNumber: data.rollNumber || '',
          parentName: data.parentName || '',
          dateOfBirth: data.dateOfBirth || '',
          email: data.email || '',
        });
      });
      setStudents(studentList);
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load students');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.id)));
    }
  };

  const handleSelectStudent = (studentId: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleTemplateSelect = (template: CertificateTemplate) => {
    setSelectedTemplate(template);
    setCustomFields({});
  };

  const getSchoolName = (): string => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.schoolName || parsed.domain || 'School Name';
      }
    } catch {
      // ignore
    }
    return 'School Name';
  };

  const handlePreview = (student: Student) => {
    setPreviewStudent(student);
    setShowPreview(true);
  };

  const handleDownloadPdf = async (student: Student) => {
    const certData: CertificateData = {
      studentName: student.name,
      programName: pdfFields.programName,
      date: pdfFields.eventDate,
      schoolName: getSchoolName(),
      templateUrl: selectedTemplate?.templateUrl,
      directorName: pdfFields.directorName || 'Director Name',
      directorTitle: pdfFields.directorTitle,
      trainerName: pdfFields.trainerName || 'Trainer Name',
      trainerTitle: pdfFields.trainerTitle
    };
    // Pass the stored base64 and overlay mode
    const doc = await generateParticipationCertificate(certData, selectedTemplate?.templateBase64, overlayMode);
    const name = `Certificate_${student.name.replace(/\s+/g, '_')}.pdf`;
    doc.save(name);
  };

  const handlePreviewPdf = async (student: Student) => {
    const certData: CertificateData = {
      studentName: student.name,
      programName: pdfFields.programName,
      date: pdfFields.eventDate,
      schoolName: getSchoolName(),
      templateUrl: selectedTemplate?.templateUrl,
      directorName: pdfFields.directorName || 'Director Name',
      directorTitle: pdfFields.directorTitle,
      trainerName: pdfFields.trainerName || 'Trainer Name',
      trainerTitle: pdfFields.trainerTitle
    };
    // Pass the stored base64 and overlay mode
    const doc = await generateParticipationCertificate(certData, selectedTemplate?.templateBase64, overlayMode);
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  };

  const handleBulkDownloadPdf = async () => {
    const selectedStudentList = filteredStudents.filter(s => selectedStudents.has(s.id));
    for (let i = 0; i < selectedStudentList.length; i++) {
      await handleDownloadPdf(selectedStudentList[i]);
      // Small delay between downloads to prevent browser blocking
      if (i < selectedStudentList.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  const generateCertificates = async () => {
    if (selectedStudents.size === 0) {
      setError('Please select at least one student');
      return;
    }

    setGenerating(true);
    setError('');
    setSuccess('');

    try {
      const selectedStudentList = filteredStudents.filter(s => selectedStudents.has(s.id));

      let successCount = 0;
      let failCount = 0;

      for (const student of selectedStudentList) {
        try {
          // 1. Generate PDF using jsPDF with stored base64 template
          const certData: CertificateData = {
            studentName: student.name,
            programName: pdfFields.programName,
            date: pdfFields.eventDate,
            schoolName: getSchoolName(),
            templateUrl: selectedTemplate?.templateUrl,
            directorName: pdfFields.directorName || 'Director Name',
            directorTitle: pdfFields.directorTitle,
            trainerName: pdfFields.trainerName || 'Trainer Name',
            trainerTitle: pdfFields.trainerTitle
          };
          // Use stored base64 and overlay mode
          const doc = await generateParticipationCertificate(certData, selectedTemplate?.templateBase64, overlayMode);
          const pdfBlob = doc.output('blob');

          // 2. Upload PDF to Firebase Storage
          const timestamp = Date.now();
          const templateLabel = selectedTemplate?.name || 'Certificate';
          const storagePath = `${domain}/certificates/${student.id}/${templateLabel}_${timestamp}.pdf`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, pdfBlob);
          const pdfUrl = await getDownloadURL(storageRef);

          // 3. Save certificate record to student's subcollection
          const studentCertsRef = collection(db, `school-users/${domain}/students/${student.id}/certificates`);
          const certificateData = {
            studentName: student.name,
            enrollmentId: student.enrollmentId,
            class: student.class,
            section: student.section,
            rollNumber: student.rollNumber,
            templateId: selectedTemplate?.id || '',
            templateName: selectedTemplate?.name || 'PDF Certificate',
            programName: pdfFields.programName,
            eventDate: pdfFields.eventDate,
            pdfUrl: pdfUrl,
            storagePath: storagePath,
            customFields: customFields,
            generatedAt: Timestamp.now(),
            generatedBy: localStorage.getItem('userUID') || 'unknown',
          };

          await addDoc(studentCertsRef, certificateData);
          successCount++;
        } catch (err) {
          console.error(`Error generating certificate for ${student.name}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully generated ${successCount} certificate(s)${failCount > 0 ? `. ${failCount} failed.` : ''}`);
        setSelectedStudents(new Set());
      } else {
        setError('Failed to generate any certificates');
      }
    } catch (err) {
      console.error('Error generating certificates:', err);
      setError('Failed to generate certificates');
    } finally {
      setGenerating(false);
    }
  };

  const getCustomFieldLabel = (key: string): string => {
    // Convert camelCase to Title Case
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <div>
      {/* Error/Success Alerts */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      <div className="row g-4">
        {/* Left Column - Selection */}
        <div className="col-lg-5">
          {/* Step 1: Template Selection */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <span className="badge bg-primary me-2">1</span>
                Select Certificate Template
              </h6>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary"></div>
                  <span className="ms-2">Loading templates...</span>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-3 text-muted">
                  <i className="bi bi-file-earmark-x fs-3 d-block mb-2"></i>
                  <p className="mb-0">No templates found. Please upload templates first.</p>
                </div>
              ) : (
                <div className="row g-2">
                  {templates.map((template) => (
                    <div key={template.id} className="col-6">
                      <div
                        className={`card h-100 cursor-pointer ${selectedTemplate?.id === template.id ? 'border-primary border-2' : 'border'}`}
                        onClick={() => handleTemplateSelect(template)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-body p-2 text-center">
                          <div
                            className="bg-light rounded mb-2 d-flex align-items-center justify-content-center"
                            style={{ height: '60px' }}
                          >
                            {template.templateUrl ? (
                              <img
                                src={template.templateUrl}
                                alt={template.name}
                                style={{ maxHeight: '55px', maxWidth: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <i className="bi bi-file-earmark-image fs-4 text-muted"></i>
                            )}
                          </div>
                          <small className="fw-semibold d-block text-truncate">{template.name}</small>
                          {selectedTemplate?.id === template.id && (
                            <i className="bi bi-check-circle-fill text-primary mt-1"></i>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Student Selection Mode */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <span className="badge bg-primary me-2">2</span>
                Select Students
              </h6>
            </div>
            <div className="card-body">
              {/* Selection Mode Toggle */}
              <div className="btn-group w-100 mb-3" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="selectionMode"
                  id="modeClass"
                  checked={selectionMode === 'class'}
                  onChange={() => setSelectionMode('class')}
                />
                <label className="btn btn-outline-primary" htmlFor="modeClass">
                  <i className="bi bi-people me-2"></i>By Class & Section
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="selectionMode"
                  id="modeEnrollment"
                  checked={selectionMode === 'enrollment'}
                  onChange={() => setSelectionMode('enrollment')}
                />
                <label className="btn btn-outline-primary" htmlFor="modeEnrollment">
                  <i className="bi bi-search me-2"></i>By Enrollment ID
                </label>
              </div>

              {selectionMode === 'class' ? (
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">Class</label>
                    <select
                      className="form-select"
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                    >
                      <option value="">Select Class</option>
                      {classes.map((c) => (
                        <option key={c} value={c}>Class {c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Section</label>
                    <select
                      className="form-select"
                      value={selectedSection}
                      onChange={(e) => setSelectedSection(e.target.value)}
                      disabled={!selectedClass}
                    >
                      <option value="">Select Section</option>
                      {sections.map((s) => (
                        <option key={s} value={s}>Section {s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="form-label small">Search by Enrollment ID or Name</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter enrollment ID or name..."
                      value={enrollmentSearch}
                      onChange={(e) => setEnrollmentSearch(e.target.value)}
                    />
                    {enrollmentSearch && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setEnrollmentSearch('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {loadingStudents && (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary"></div>
                  <span className="ms-2">Loading students...</span>
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Custom Fields (if any) */}
          {selectedTemplate && Object.keys(customFields).length > 0 && (
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <h6 className="mb-0">
                  <span className="badge bg-primary me-2">3</span>
                  Additional Information
                </h6>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Fill in the following fields for the certificate:
                </p>
                <div className="row g-3">
                  {Object.keys(customFields).map((key) => (
                    <div key={key} className="col-12">
                      <label className="form-label small">{getCustomFieldLabel(key)}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={customFields[key]}
                        onChange={(e) => setCustomFields({ ...customFields, [key]: e.target.value })}
                        placeholder={`Enter ${getCustomFieldLabel(key).toLowerCase()}...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* PDF Certificate Settings */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <i className="bi bi-file-earmark-pdf text-danger me-2"></i>
                PDF Certificate Settings
              </h6>
            </div>
            <div className="card-body">
              {/* Overlay Mode Toggle */}
              <div className="mb-3">
                <label className="form-label small fw-semibold">Text Overlay Mode</label>
                <div className="btn-group w-100" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="overlayMode"
                    id="modeStudentOnly"
                    checked={overlayMode === 'studentNameOnly'}
                    onChange={() => setOverlayMode('studentNameOnly')}
                  />
                  <label className="btn btn-outline-secondary" htmlFor="modeStudentOnly">
                    <i className="bi bi-person me-1"></i>
                    Student Name Only
                  </label>
                  <input
                    type="radio"
                    className="btn-check"
                    name="overlayMode"
                    id="modeAllFields"
                    checked={overlayMode === 'allFields'}
                    onChange={() => setOverlayMode('allFields')}
                  />
                  <label className="btn btn-outline-secondary" htmlFor="modeAllFields">
                    <i className="bi bi-card-text me-1"></i>
                    All Fields
                  </label>
                </div>
                <small className="text-muted d-block mt-1">
                  {overlayMode === 'studentNameOnly'
                    ? 'Only the student name will be overlaid. Other text comes from your template image.'
                    : 'All fields (name, program, date, signatures) will be overlaid on the template.'}
                </small>
              </div>
              <hr />
              <div className={`row g-3 ${overlayMode === 'studentNameOnly' ? 'opacity-50' : ''}`}>
                <div className="col-12">
                  <label className="form-label small">Program/Event Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pdfFields.programName}
                    onChange={(e) => setPdfFields({ ...pdfFields, programName: e.target.value })}
                    placeholder="e.g., Student Innovation and Leadership Development Program"
                    disabled={overlayMode === 'studentNameOnly'}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">Event Date</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pdfFields.eventDate}
                    onChange={(e) => setPdfFields({ ...pdfFields, eventDate: e.target.value })}
                    placeholder="e.g., 11 November 2024"
                    disabled={overlayMode === 'studentNameOnly'}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Director/Signatory 1 Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pdfFields.directorName}
                    onChange={(e) => setPdfFields({ ...pdfFields, directorName: e.target.value })}
                    placeholder="e.g., Jamie Chastain"
                    disabled={overlayMode === 'studentNameOnly'}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pdfFields.directorTitle}
                    onChange={(e) => setPdfFields({ ...pdfFields, directorTitle: e.target.value })}
                    placeholder="e.g., Program Director"
                    disabled={overlayMode === 'studentNameOnly'}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Trainer/Signatory 2 Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pdfFields.trainerName}
                    onChange={(e) => setPdfFields({ ...pdfFields, trainerName: e.target.value })}
                    placeholder="e.g., Benjamin Shah"
                    disabled={overlayMode === 'studentNameOnly'}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pdfFields.trainerTitle}
                    onChange={(e) => setPdfFields({ ...pdfFields, trainerTitle: e.target.value })}
                    placeholder="e.g., Workshop Trainer"
                    disabled={overlayMode === 'studentNameOnly'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Student List */}
        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="bi bi-people me-2"></i>
                Students
                {filteredStudents.length > 0 && (
                  <span className="badge bg-secondary ms-2">{filteredStudents.length}</span>
                )}
              </h6>
              {filteredStudents.length > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">
                    {selectedStudents.size} selected
                  </span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleSelectAll}
                  >
                    {selectedStudents.size === filteredStudents.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              )}
            </div>
            <div className="card-body p-0">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-person-x fs-1 d-block mb-3"></i>
                  <p className="mb-0">
                    {selectionMode === 'class'
                      ? 'Select a class and section to view students'
                      : 'Enter an enrollment ID or name to search'
                    }
                  </p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table className="table table-hover mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedStudents.size === filteredStudents.length && filteredStudents.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Student</th>
                        <th>Enrollment ID</th>
                        <th>Class</th>
                        <th style={{ width: '140px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student) => (
                        <tr
                          key={student.id}
                          className={selectedStudents.has(student.id) ? 'table-primary' : ''}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedStudents.has(student.id)}
                              onChange={() => handleSelectStudent(student.id)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                                <i className="bi bi-person"></i>
                              </div>
                              <div>
                                <div className="fw-semibold">{student.name}</div>
                                <small className="text-muted">Roll: {student.rollNumber}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <code className="small">{student.enrollmentId}</code>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark">
                              {student.class}-{student.section}
                            </span>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-secondary"
                                onClick={() => handlePreview(student)}
                                disabled={!selectedTemplate}
                                title="Preview Data"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handlePreviewPdf(student)}
                                title="Preview PDF"
                              >
                                <i className="bi bi-file-earmark-pdf"></i>
                              </button>
                              <button
                                className="btn btn-outline-success"
                                onClick={() => handleDownloadPdf(student)}
                                title="Download PDF"
                              >
                                <i className="bi bi-download"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {filteredStudents.length > 0 && (
              <div className="card-footer bg-white">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span className="text-muted">
                    {selectedStudents.size} of {filteredStudents.length} students selected
                  </span>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-danger"
                      onClick={handleBulkDownloadPdf}
                      disabled={selectedStudents.size === 0}
                      title="Download PDF certificates for selected students"
                    >
                      <i className="bi bi-file-earmark-pdf me-2"></i>
                      Download PDFs ({selectedStudents.size})
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={generateCertificates}
                      disabled={generating || selectedStudents.size === 0}
                    >
                      {generating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-award me-2"></i>
                          Save to Records ({selectedStudents.size})
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewStudent && selectedTemplate && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-eye me-2"></i>
                  Certificate Preview - {previewStudent.name}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowPreview(false)}></button>
              </div>
              <div className="modal-body">
                <div className="bg-light p-4 rounded">
                  {selectedTemplate.templateUrl && (
                    <div className="text-center mb-4">
                      <img
                        src={selectedTemplate.templateUrl}
                        alt="Template"
                        className="img-fluid border rounded"
                        style={{ maxHeight: '300px' }}
                      />
                    </div>
                  )}
                  <div className="alert alert-info">
                    <h6 className="alert-heading">
                      <i className="bi bi-info-circle me-2"></i>
                      Certificate Data
                    </h6>
                    <hr />
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Student Name:</strong> {previewStudent.name}</p>
                        <p className="mb-1"><strong>Enrollment ID:</strong> {previewStudent.enrollmentId}</p>
                        <p className="mb-1"><strong>Class:</strong> {previewStudent.class}</p>
                        <p className="mb-1"><strong>Section:</strong> {previewStudent.section}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Roll Number:</strong> {previewStudent.rollNumber}</p>
                        <p className="mb-1"><strong>Parent Name:</strong> {previewStudent.parentName || 'N/A'}</p>
                        <p className="mb-1"><strong>Date of Birth:</strong> {previewStudent.dateOfBirth || 'N/A'}</p>
                        <p className="mb-1"><strong>School:</strong> {getSchoolName()}</p>
                      </div>
                    </div>
                    {Object.keys(customFields).length > 0 && (
                      <>
                        <hr />
                        <div className="row">
                          {Object.entries(customFields).map(([key, value]) => (
                            <div key={key} className="col-md-6">
                              <p className="mb-1">
                                <strong>{getCustomFieldLabel(key)}:</strong> {value || '[Not Set]'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-muted small mb-0">
                    <i className="bi bi-lightbulb me-1"></i>
                    The placeholders in your template will be replaced with the above data when the certificate is generated.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for certificate generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default GenerateCertificate;
